import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';
import { type QuizGenerateItem } from '@/api/ai/type';
import { type AchievementStandard } from '@/shared/features/ai-quiz-generation/types';
import { ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils';
import { useResultSectionScroll } from '@/views/build/features/ai-quiz-generation/hooks/useResultSectionScroll';
import { BuildGenerateAction, type GenerateStatus } from '@/views/build/modal/BuildAiModal/components/BuildAiGenerateAction';
import { useAiGeneratedQuizItem } from '../../hook/useAiGeneratedQuizItem';
import { AiTrialBadge } from './components/AiTrialBadge';
import { BuildAiModalBody } from './components/BuildAiModalBody';
import { BuildAiModalFooter } from './components/BuildAiModalFooter';
import { BuildAiModalHeader } from './components/BuildAiModalHeader';
import { BuildAiResultSection } from './components/BuildAiResultSection';
import { type GenerationMode } from './types';
interface BuildAiModalBaseProps {
  /** 액션 관련 콜백 그룹 */
  actions: {
    onClose: () => void;
    onGenerate?: () => void;
    onSave: (selectedItems: QuizGenerateItem[]) => void;
    onDelete?: (item: QuizGenerateItem) => void;
  };

  /** 렌더링할 컨텐츠 그룹 */
  content: {
    pendingFallback: ReactNode;
    promptContent: ReactNode;
  };

  /** 생성 상태 및 데이터 그룹 */
  generation: {
    status: GenerateStatus;
    items: QuizGenerateItem[];
    quizCount: number;
  };

  /** 쿼터 제한 상태 (guest / FREE plan) */
  guestTrial: {
    isQuotaLimited: boolean;
    remaining?: number;
    lifetimeLimit: number;
    isQuotaExhausted: boolean;
    isLoading?: boolean;
  };

  /** 퀴즈 개수 제한 */
  quizCountLimit: number;

  /** 선택된 성취기준 */
  selectedAchievementStandards: AchievementStandard[];

  /** AI 문제 생성 모드 (빠르게/꼼꼼하게) */
  generationMode?: {
    selected: GenerationMode;
    onChange: (mode: GenerationMode) => void;
    /** Plan 권한 부족으로 잠긴 모드. dropdown 에 PlanBadge 가 노출된다. */
    locked?: readonly GenerationMode[];
    /** Chevron coach-mark 툴팁 노출 여부 (최초 생성 흐름에서 인풋에 텍스트 입력 시). */
    shouldShowCoachMark?: boolean;
  };
}
export const BuildAiModalBase: React.FC<BuildAiModalBaseProps> = props => {
  const {
    selectedItems,
    savedItems,
    unSavedItems,
    handleSelectItem,
    handleSelectItemsAtOnce,
    handleSaveItems,
    handleDeleteItem: removeFromSelection
  } = useAiGeneratedQuizItem(props.generation.items, {
    countLimit: props.quizCountLimit
  });
  const resultSectionRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const isGenerated = useMemo(() => props.generation.status === 'success' || props.generation.status === 'regenerating', [props.generation.status]);
  const handleDeleteItem = (item: QuizGenerateItem) => {
    removeFromSelection(item); // 선택된 아이템 리스트에서 삭제
    props.actions.onDelete?.(item); // 실제 렌더링되는 아이템에서 삭제
  };
  const handleSelectAll = useCallback(() => {
    selectedItems.length > 0 ? handleSelectItemsAtOnce([]) : handleSelectItemsAtOnce(props.generation.items);
  }, [selectedItems.length, handleSelectItemsAtOnce, props.generation.items]);

  // 재생성 시 결과 섹션으로 자동 스크롤
  useResultSectionScroll(props.generation.status, resultSectionRef, bodyRef);

  // 유료 유저이거나, 쿼터 제한 유저인데 로딩 완료 + 쿼터 남아있을 때 생성 버튼 표시
  const shouldShowGenerateAction = !props.guestTrial.isQuotaLimited || !props.guestTrial.isLoading && !props.guestTrial.isQuotaExhausted;
  return <ResponsiveDialog onClose={props.actions.onClose} size={'2xl'} shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="BuildAiModalBase" data-sentry-source-file="BuildAiModalBase.tsx">
      <BuildAiModalHeader onClose={props.actions.onClose} data-sentry-element="BuildAiModalHeader" data-sentry-source-file="BuildAiModalBase.tsx" />
      <BuildAiModalBody ref={bodyRef} data-sentry-element="BuildAiModalBody" data-sentry-source-file="BuildAiModalBase.tsx">
        {props.generation.status === 'generating' ? props.content.pendingFallback : <div className="flex h-full flex-col tablet-sm:flex-row">
            <div className={cn('flex shrink-0 grow flex-col gap-lg overflow-y-auto pb-md', LAYOUT_WIDTHS.PROMPT_DEFAULT, isGenerated && LAYOUT_WIDTHS.PROMPT_WITH_RESULT)}>
              <div className={cn('flex flex-col px-md tablet-sm:px-lg pb-lg tablet-sm:pb-0 flex-1 relative justify-between gap-lg')}>
                {props.content.promptContent}
                <div className="flex flex-col gap-xs">
                  {shouldShowGenerateAction ? <BuildGenerateAction onGenerate={props.actions.onGenerate} onCancel={props.actions.onClose} status={props.generation.status} generationMode={props.generationMode?.selected} onGenerationModeChange={props.generationMode?.onChange} lockedModes={props.generationMode?.locked} shouldShowModeCoachMark={props.generationMode?.shouldShowCoachMark} /> : null}

                  {/* Trial Badge - 쿼터 제한 유저에게 남은 횟수 표시 (첫 생성 전에는 노출 X) */}
                  {props.guestTrial.isQuotaLimited && typeof props.guestTrial.remaining === 'number' && props.guestTrial.remaining !== -1 && props.generation.status !== 'idle' && <AiTrialBadge remaining={props.guestTrial.remaining} lifetimeLimit={props.guestTrial.lifetimeLimit} />}
                </div>
              </div>
            </div>
            <div className={cn('flex shrink-0', LAYOUT_WIDTHS.RESULT_DEFAULT, isGenerated && LAYOUT_WIDTHS.RESULT_EXPANDED)}>
              <BuildAiResultSection ref={resultSectionRef} generateStatus={props.generation.status} generatedItems={props.generation.items} pendingFallback={props.content.pendingFallback} quizCount={props.generation.quizCount} selection={{
            selectedItems,
            savedItems,
            unSavedItems,
            onSelect: handleSelectItem,
            onSelectAll: handleSelectAll,
            onDelete: handleDeleteItem
          }} achievementStandards={props.selectedAchievementStandards} className="flex-1" />
            </div>
          </div>}
      </BuildAiModalBody>

      <BuildAiModalFooter status={props.generation.status} onSave={() => {
      handleSaveItems(selectedItems);
      props.actions.onSave(selectedItems);
    }} onGenerate={props.actions.onGenerate} onCancel={props.actions.onClose} saveDisabled={selectedItems.length === 0} isQuotaLimited={props.guestTrial.isQuotaLimited} isQuotaExhausted={props.guestTrial.isQuotaExhausted} isLoadingQuota={props.guestTrial.isLoading} generationMode={props.generationMode?.selected} onGenerationModeChange={props.generationMode?.onChange} lockedModes={props.generationMode?.locked} shouldShowModeCoachMark={props.generationMode?.shouldShowCoachMark} data-sentry-element="BuildAiModalFooter" data-sentry-source-file="BuildAiModalBase.tsx" />
    </ResponsiveDialog>;
};

/**
 * BuildAiModalBase 레이아웃 상수
 *
 * 반응형 레이아웃 width 계산:
 * - 생성 전/실패: Prompt 56% | (빈 공간 44%)
 * - 생성 후: Prompt 44% | Result 56%
 */
const LAYOUT_WIDTHS = {
  /** Prompt 섹션 기본 width (생성 전) */
  PROMPT_DEFAULT: 'w-full tablet-sm:w-[56%]',
  /** Prompt 섹션 width (결과 있을 때) */
  PROMPT_WITH_RESULT: 'tablet-sm:w-[44%]',
  /** Result 섹션 기본 width */
  RESULT_DEFAULT: 'w-[44%]',
  /** Result 섹션 width (확장 시) */
  RESULT_EXPANDED: 'w-full tablet-sm:w-[56%]'
} as const;